import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import {Router} from '@angular/router';
import {FpaScenarioDialogComponent} from '../dialogs/fpa-scenario-dialog/fpa-scenario-dialog.component';
import {FpaSubmissionDialogComponent} from '../dialogs/fpa-submission-dialog/fpa-submission-dialog.component';
import {PersonLogin, RoleName} from '../person/person-login';
import {EntityService} from '../services/entity-service';
import {FpaService} from '../services/fpa-service';
import {PersonLoginService} from '../services/person-login-service';
import {StringUtils} from '../utils/string-utils';
import {FpaEntity} from './fpa-entity';
import {FpaScenario} from './fpa-scenario';
import {MatDialog} from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DecimalPipe, PercentPipe, CurrencyPipe } from '@angular/common';
import { LeadTimeUtils } from '../utils/lead-time-utils';

@Component({
    selector: 'fpa-management',
    templateUrl: './fpa-management.component.html',
    styleUrls: ['./fpa-management.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatButton, MatIcon, FormsModule, DecimalPipe, PercentPipe, CurrencyPipe]
})
export class FpaManagementComponent implements OnInit, OnChanges {
  readonly entityType = input.required<string>({ alias: 'entity-type' });
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly entityService = inject(EntityService);
  private readonly fpaService = inject(FpaService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private personLogin!: PersonLogin;
  readonly fpaEntity = signal<FpaEntity>(undefined!);
  public fpaAdmin!: boolean;
  public isFpaApprover!: boolean;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly selectedCustomScenario = signal<FpaScenario>(undefined!);
  readonly selectedStandardScenario = signal<FpaScenario>(undefined!);
  readonly standardBuyResellScenarios = signal<FpaScenario[]>(undefined!);

  public selectStandardScenario(scenario: FpaScenario) {
    this.selectedCustomScenario.set(undefined!);
    this.selectedStandardScenario.set(scenario);
    this.updateFpaEntitySelectedValues();
  }

  private updateFpaEntitySelectedValues(): void {
    let scenario: FpaScenario = this.selectedCustomScenario() || this.selectedStandardScenario();
    if (scenario) {
      let finalMultiplier = isNaN(+scenario.requestedMultiplier) ? 0 : +scenario.requestedMultiplier;
      this.fpaEntity().finalMultiplier = finalMultiplier;
      this.fpaEntity().commissionAmount = this.getCommissionAmount(scenario);
      this.fpaEntity().commissionPercent = scenario.commissionPercent;
      this.fpaEntity().selectedScenarioId = scenario.customScenarioId;
    }
  }

  public selectCustomScenario(scenario: FpaScenario) {
    this.selectedStandardScenario.set(undefined!);
    this.selectedCustomScenario.set(scenario);
    this.updateFpaEntitySelectedValues();
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public getSelectedScenario(): FpaScenario {
    return this.selectedCustomScenario() || this.selectedStandardScenario();
  }

  public applySelection() {
    this.isBusy.set(true);
    let scenario = this.getSelectedScenario();
    if (scenario) {
      this.fpaService.applySelection(scenario).subscribe(result => {
        this.backToEntity();
      }, error => this.handleError(error));
    } else {
      this.isBusy.set(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }

  private initialize(): void {
    const typeString: string = this.entityType();
    const idString: string = this.entityId();
    const revisionString: string = this.entityRevision();
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      this.fpaService.getFpaEntity(typeString, idString, +revisionString).subscribe({
        next: (result: FpaEntity) => {
          this.fpaEntity.set(result);
          if (LeadTimeUtils.isQuickShip(result.leadTime)) {
            this.backToEntity();
          } else {
            this.standardBuyResellScenarios.set(this.fpaEntity().standardScenarios.filter(s => s.saleType === 'Buy/Resell'));
            if (this.fpaEntity().selectedScenarioId) {
              let scenario = this.fpaEntity().customScenarios.filter(s => s.customScenarioId === this.fpaEntity().selectedScenarioId)[0];
              if (scenario) {
                this.selectCustomScenario(scenario);
              }
            } else {
              let scenario = this.fpaEntity().standardScenarios.filter(s => s.multiplier === this.fpaEntity().finalMultiplier)[0];
              if (scenario) {
                this.selectStandardScenario(scenario);
              }
            }
            this.isBusy.set(false);
          }
        }, error: (error) => this.handleError(error)
      });
    } else {
      this.backToMain();
    }
  }

  public backToEntity(): void {
    let pageName = this.fpaEntity().entityKey.entityName === 'QUOTE' ? "quotes" : "orders";
    this.router.navigate([pageName, this.fpaEntity().entityKey.entityId, this.fpaEntity().entityKey.entityRevision]);
  }

  public getNetAmount(scenario: FpaScenario): number {
    let requestedMultiplier = isNaN(+scenario.requestedMultiplier) ? 0 : +scenario.requestedMultiplier;
    let totalList = scenario.totalList || 0;
    return requestedMultiplier * totalList;
  }

  public getPoAmount(scenario: FpaScenario): number {
    return this.getNetAmount(scenario) + (scenario.shippingCharge || 0) + (scenario.specialCharges || 0);
  }

  public getCommissionAmount(scenario: FpaScenario): number {
    return this.getNetAmount(scenario) * (scenario.commissionPercent || 0);
  }

  public getOverrideCommissionAmount(scenario: FpaScenario): number {
    return this.getNetAmount(scenario) * (scenario.overrideCommissionPercent || 0);
  }

  public save(): void {
    this.resetStatus();
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
  }

  private backToMain(): void {
    this.router.navigate(['main']);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.fpaAdmin = this.personLoginService.hasRole(RoleName.IECIT) || this.personLoginService.isRsm();
      this.initialize();
    }
  }

  public deleteCustomScenario(): void {
    this.isBusy.set(true);
    this.fpaService.deleteScenario(this.selectedCustomScenario().customScenarioId).subscribe(() => {
      this.isBusy.set(false);
      this.fpaEntity().customScenarios = [];
      this.selectedCustomScenario.set(undefined!);
    }, error => this.handleError(error));
  }

  public editCustomScenario(): void {
    if (this.selectedCustomScenario() && this.selectedCustomScenario().saleType) {
      let customScenario: FpaScenario = JSON.parse(JSON.stringify(this.selectedCustomScenario()));
      if (customScenario.saleType == 'B') {
        customScenario.poAmount = null!;
      } else if (customScenario.saleType == 'C') {
        customScenario.poAmount = this.getPoAmount(this.selectedCustomScenario()).toFixed(2).toString();
        customScenario.requestedMultiplier = null!;
      }
      this.openScenarioDialog(customScenario);
    }
  }

  private openScenarioDialog(scenario: FpaScenario) {
    let scenarioDialog = this.dialog.open(FpaScenarioDialogComponent, {
      width: '440px',
      height: '520px',
      data: {scenario: scenario, fpaEntity: this.fpaEntity()},
    });
    scenarioDialog.afterClosed().subscribe(result => {
      if (result) {
        this.fpaEntity.set(result);
        this.selectedCustomScenario.set(undefined!);
      }
    });
  }

  public addCustomScenario(): void {
    let scenario = new FpaScenario();
    scenario.saleType = this.fpaEntity().saleType;
    scenario.multiplier = this.fpaEntity().publishedMultiplier;
    scenario.entityKey = JSON.parse(JSON.stringify(this.fpaEntity().entityKey));
    this.openScenarioDialog(scenario);
  }

  public submitForApproval(): void {
    let submitDialog = this.dialog.open(FpaSubmissionDialogComponent, {
      width: '580px',
      height: '420px',
      data: {entityId: this.fpaEntity().entityKey.entityId, entityRevision: this.fpaEntity().entityKey.entityRevision},
    });
    submitDialog.afterClosed().subscribe(result => {
      if (result) {
        this.backToEntity();
      }
    });
  }

  public canSubmit(): boolean {
    return this.fpaEntity().customScenarios.length === 1 && this.fpaEntity().customScenarios[0].approvalStatus == 'Pending Submission';
  }

  public canOverrideCommission(): boolean {
    return !this.isBusy() && this.selectedCustomScenario() && this.selectedCustomScenario().saleType == 'C' && this.validOverrideCommissionEntered();
  }

  private validOverrideCommissionEntered() {
    return !this.selectedCustomScenario().overrideCommissionPercent || !isNaN(+this.selectedCustomScenario().overrideCommissionPercent);
  }

  public overrideCommission(): void {
    this.isBusy.set(true);
    if (this.selectedCustomScenario().overrideCommissionPercent && !isNaN(+this.selectedCustomScenario().overrideCommissionPercent)) {
      let overridePercent = +this.selectedCustomScenario().overrideCommissionPercent;
      if (overridePercent > 1) {
        overridePercent = overridePercent / 100;
        this.selectedCustomScenario().overrideCommissionPercent = overridePercent;
      }
    }
    this.fpaService.overrideCommission(this.selectedCustomScenario()).subscribe(() => this.isBusy.set(false), error => this.handleError(error));
  }

}
